<template>
  <div class="news">
    <!-- 顶部标题区域 -->
    <section class="products-header">
      <div class="container">
        <h1 class="page-title">
          <span class="title-glow">新闻中心</span>
        </h1>
        <p class="page-subtitle">了解最新行业动态和公司资讯</p>
      </div>
    </section>

    <!-- 主内容区域 -->
    <section class="news-main">
      <div class="container">
        <!-- 分类标签页 -->
        <div class="news-tabs">
          <button 
            v-for="(tab, index) in newsTabs" 
            :key="index"
            class="news-tab"
            :class="{ active: activeTab === index }"
            @click="switchTab(index)"
          >
            {{ tab.name }}
          </button>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <p>加载中...</p>
        </div>
        <!-- 错误提示 -->
        <div v-else-if="error" class="error-container">
          <p>{{ error }}</p>
        </div>
        <!-- 新闻网格列表 -->
        <div v-else class="news-grid-container">
          <transition name="fade" mode="out-in">
            <div :key="activeTab" class="news-grid">
              <div 
                v-for="(newsItem, index) in paginatedNewsList" 
                :key="index"
                class="news-card"
                @click="goDetail(newsItem)"
              >
                <div class="news-image">
                  <img :src="resolveNewsImage(newsItem.image)" :alt="newsItem.title" />
                </div>
                <div class="news-content">
                  <h3 class="news-title">{{ newsItem.title }}</h3>
                  <p class="news-summary">{{ newsItem.summary }}</p>
                  <div class="news-date">TIME: {{ formatDate(newsItem.date) }}</div>
                </div>
              </div>
              
              <!-- 空状态 -->
              <div v-if="paginatedNewsList.length === 0" class="empty-state">
                <p>暂无{{ newsTabs[activeTab].name }}</p>
              </div>
            </div>
          </transition>
        </div>

        <!-- 分页控件 -->
        <div class="news-pagination" v-if="!loading && !error && totalItems > 0">
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="currentPage"
            :page-size="pageSize"
            :total="totalItems"
            @current-change="handlePageChange"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios'

// 使用 require.context 让 webpack 收集新闻图片资源（支持 jpg 和 png）
const newsImageCtx = require.context('@/assets/news_photo', false, /\.(png|jpe?g)$/)

export default {
  name: 'News',
  data() {
    return {
      activeTab: 0, // 默认选中第一个：公司新闻
      currentPage: 1, // 当前页码
      pageSize: 6, // 每页显示数量
      newsTabs: [
        { name: '公司新闻', value: 'company' },
        { name: '行业新闻', value: 'industry' }
      ],
      newsList: [],
      loading: false,
      error: null
    }
  },
  computed: {
    filteredNewsList() {
      const currentTab = this.newsTabs[this.activeTab]
      // 按当前分类过滤，保持接口返回顺序（后端已按日期倒序排好）
      return this.newsList.filter(news => news.type === currentTab.value)
    },
    totalItems() {
      return this.filteredNewsList.length
    },
    totalPages() {
      return Math.ceil(this.filteredNewsList.length / this.pageSize);
    },
    paginatedNewsList() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredNewsList.slice(start, end);
    }
  },
  methods: {
    switchTab(index) {
      this.activeTab = index;
      this.currentPage = 1; // 切换分类时重置到第一页
    },
    handlePageChange(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        // 滚动到顶部
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    async fetchNewsList() {
      try {
        this.loading = true
        this.error = null
        const response = await axios.get('/api/news/list')
        if (response.data.code === 200) {
          const resData = response.data && response.data.data
          let list = []

          if (Array.isArray(resData)) {
            // API 模式：后端已经返回扁平数组，并带有 type 字段
            list = resData
          } else if (resData && typeof resData === 'object') {
            // 静态 JSON 或旧格式：company / industry 两个分组
            const companyNews = Array.isArray(resData.company)
              ? resData.company.map(item => ({ ...item, type: item.type || 'company' }))
              : []
            const industryNews = Array.isArray(resData.industry)
              ? resData.industry.map(item => ({ ...item, type: item.type || 'industry' }))
              : []
            list = [...companyNews, ...industryNews]
          }

          this.newsList = list
        } else {
          this.error = response.data.message || '获取新闻列表失败'
          this.newsList = []
        }
      } catch (error) {
        console.error('获取新闻列表失败:', error)
        this.error = '网络请求失败，请检查服务器是否运行'
        this.newsList = []
      } finally {
        this.loading = false
      }
    },
    resolveNewsImage(imagePath) {
      if (!imagePath) return ''
      if (imagePath.startsWith('@/assets/news_photo/')) {
        const fileName = imagePath.split('/').pop()
        try {
          return newsImageCtx('./' + fileName)
        } catch (e) {
          console.warn('新闻图片未找到:', fileName, e)
          return ''
        }
      }
      // 其他情况（远程 URL 等）直接返回
      return imagePath
    },
    goDetail(item) {
      if (item && item.id) {
        // 计算在当前分类下的前一篇和后一篇（按当前过滤顺序）
        const list = this.filteredNewsList
        const idx = list.findIndex(n => n.id === item.id)
        const prev = idx > 0 ? list[idx - 1] : null
        const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null

        // 跳转到详情页并附带相邻文章信息
        this.$router.push({
          path: '/news/detail',
          query: {
            id: item.id,
            ...(prev ? { prevId: prev.id } : {}),
            ...(next ? { nextId: next.id } : {})
          }
        })
      } else {
        console.warn('新闻项缺少ID，无法跳转到详情页', item)
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    syncTabWithHash() {
      const hash = this.$route.hash;
      if (hash === '#company-news' || hash === '#公司新闻') {
        this.activeTab = 0;
      } else if (hash === '#industry-news' || hash === '#行业动态') {
        this.activeTab = 1;
      }
    }
  },
  watch: {
    activeTab() {
      this.currentPage = 1; // 切换分类时重置页码
    },
    '$route.hash'() {
      // 监听路由 hash 变化，支持通过 URL hash 跳转到指定分类
      this.syncTabWithHash();
    }
  },
  async mounted() {
    await this.fetchNewsList()
    // 支持通过 URL hash 跳转到指定分类
    this.syncTabWithHash();
  }
}
</script>

<style scoped>
.news {
  /* min-height: 100vh; */
  background: #f5f7fa;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 顶部标题区域 */
.products-header {
  position: relative;
  background-image: 
    url('../../assets/background_header/新闻中心.png');
    /* linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f1419 100%); */
  background-size: cover, 100%;
  background-position: center, center;
  background-blend-mode: overlay;
  padding: 45px 0;
  overflow: hidden;
  min-height: 200px;
  display: flex;
  align-items: center;
}

.products-header .container {
  position: relative;
  z-index: 2;
  width: 100%;
  margin: 0;
  padding: 0 60px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
}

.page-title {
  position: relative;
  z-index: 2;
  margin: 0;
  text-align: left;
  padding: 0;
}

.page-subtitle {
  margin: 8px 0 0 0;
  text-align: left;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
  line-height: 1.8;
  opacity: 1;
  max-width: 900px;
  margin-left: 2em;
  position: relative;
  z-index: 2;
  backdrop-filter: blur(5px);
}

.title-glow {
  display: inline-block;
  font-size: 46px;
  font-weight: 900;
  font-family: 'Arial Black', 'Microsoft YaHei', sans-serif;
  letter-spacing: 5px;
  color: #ffffff;
  filter: drop-shadow(0 0 10px rgba(96, 165, 250, 0.6));
  position: relative;
  backdrop-filter: blur(5px);
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% center; }
  50% { background-position: 100% center; }
}

/* 主内容区域 */
.news-main {
  background: #f5f7fa;
  padding: 30px 0 60px 0;
}

/* 分类标签页 */
.news-tabs {
  display: flex;
  gap: 0;
  margin-bottom: 30px;
  background: white;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.news-tab {
  padding: 16px 32px;
  font-size: 16px;
  font-weight: 500;
  color: #666;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  text-align: center;
}

.news-tab:hover {
  color: #4c95e3;
  background: #f8f9fa;
}

.news-tab.active {
  color: #4c95e3;
  border-bottom-color: #4c95e3;
  font-weight: 600;
  background: #f8f9fa;
}

/* 新闻网格容器 */
.news-grid-container {
  background: white;
  border-radius: 0 0 8px 8px;
  padding: 30px;
  min-height: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 30px;
}

/* 新闻网格 */
.news-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

/* 过渡动画 */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 新闻卡片 */
.news-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
}

.news-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  border-color: #4c95e3;
}

/* 新闻图片 */
.news-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #f0f0f0;
  position: relative;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.news-card:hover .news-image img {
  transform: scale(1.05);
}

/* 新闻内容 */
.news-content {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.news-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.5;
  transition: color 0.3s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 54px;
}

.news-card:hover .news-title {
  color: #4c95e3;
}

.news-summary {
  color: #666;
  line-height: 1.7;
  margin: 0;
  font-size: 14px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.news-date {
  color: #999;
  font-size: 13px;
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}

.news-pagination {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.news-pagination :deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
}

/* 空状态 */
.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #999;
  font-size: 16px;
}

/* 加载和错误状态 */
.loading-container,
.error-container {
  text-align: center;
  padding: 60px 20px;
  color: #666;
  background: white;
  border-radius: 0 0 8px 8px;
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-container {
  color: #e74c3c;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .news-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (max-width: 768px) {
  .products-header {
    padding: 50px 0;
    min-height: 190px;
  }

  .title-glow {
    font-size: 32px;
    letter-spacing: 1.5px;
  }

  .products-header .container {
    padding: 0 24px;
  }

  .page-subtitle {
    margin-left: 1.5em;
    font-size: 14px;
  }

  .news-main {
    padding: 20px 0 40px 0;
  }

  .news-tabs {
    margin-bottom: 20px;
  }

  .news-tab {
    padding: 12px 20px;
    font-size: 15px;
  }

  .news-grid-container {
    padding: 20px 15px;
  }

  .news-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .news-image {
    height: 180px;
  }

  .news-title {
    font-size: 16px;
    min-height: 48px;
  }

  .news-pagination {
    padding: 15px 10px;
  }
}

@media (max-width: 480px) {
  .title-glow {
    font-size: 28px;
    letter-spacing: 1px;
  }

  .news-tabs {
    flex-direction: column;
  }

  .news-tab {
    width: 100%;
    border-bottom: 1px solid #e5e7eb;
    border-radius: 0;
  }

  .news-tab.active {
    border-bottom-color: #4c95e3;
  }

  .news-pagination {
    flex-direction: column;
    gap: 8px;
  }
}
</style>



